/**
 * NEXUS UI - ProgressArc
 * Circular progress indicator with glowing segments
 */

import React, { useMemo } from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

export interface ProgressArcProps extends BaseProps {
  value: number;
  max?: number;
  variant?: NexusVariant;
  size?: NexusSize;
  strokeWidth?: number;
  showValue?: boolean;
  showLabel?: boolean;
  label?: string;
  segments?: number;
  animated?: boolean;
  children?: React.ReactNode;
}

const variantColors: Record<NexusVariant, string> = {
  cyan: '#00d4ff',
  blue: '#3b82f6',
  violet: '#a855f7',
  green: '#00ff88',
  amber: '#ffaa00',
  red: '#ff3366',
};

const sizeConfig: Record<NexusSize, { size: number; strokeWidth: number; fontSize: string }> = {
  sm: { size: 80, strokeWidth: 4, fontSize: '1rem' },
  md: { size: 120, strokeWidth: 5, fontSize: '1.5rem' },
  lg: { size: 160, strokeWidth: 6, fontSize: '2rem' },
  xl: { size: 200, strokeWidth: 8, fontSize: '2.5rem' },
};

export const ProgressArc: React.FC<ProgressArcProps> = ({
  value,
  max = 100,
  variant = 'cyan',
  size = 'md',
  strokeWidth,
  showValue = true,
  showLabel = true,
  label,
  segments = 0,
  animated = true,
  children,
  className = '',
  style,
}) => {
  const config = sizeConfig[size];
  const actualStrokeWidth = strokeWidth ?? config.strokeWidth;
  const svgSize = config.size;
  const radius = (svgSize - actualStrokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const offset = circumference - (percentage / 100) * circumference;
  const color = variantColors[variant];

  const segmentDasharray = useMemo(() => {
    if (segments <= 0) return undefined;
    const segmentLength = circumference / segments;
    const gap = 4;
    return `${segmentLength - gap} ${gap}`;
  }, [segments, circumference]);

  const gradientId = `progress-gradient-${variant}-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div
      className={`nx-progress-arc ${className}`}
      style={{
        width: svgSize,
        height: svgSize,
        '--arc-color': color,
        ...style,
      } as React.CSSProperties}
    >
      <svg
        className="nx-progress-arc__svg"
        width={svgSize}
        height={svgSize}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.6" />
            <stop offset="100%" stopColor={color} stopOpacity="1" />
          </linearGradient>
          <filter id={`glow-${gradientId}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Track */}
        <circle
          className="nx-progress-arc__track"
          cx={svgSize / 2}
          cy={svgSize / 2}
          r={radius}
          strokeWidth={actualStrokeWidth}
          strokeDasharray={segmentDasharray}
        />

        {/* Glow layer */}
        <circle
          className="nx-progress-arc__glow"
          cx={svgSize / 2}
          cy={svgSize / 2}
          r={radius}
          strokeWidth={actualStrokeWidth + 6}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: animated ? 'stroke-dashoffset 0.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            stroke: color,
            opacity: 0.3,
            filter: 'blur(6px)',
          }}
        />

        {/* Progress */}
        <circle
          className="nx-progress-arc__progress"
          cx={svgSize / 2}
          cy={svgSize / 2}
          r={radius}
          strokeWidth={actualStrokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          stroke={`url(#${gradientId})`}
          filter={`url(#glow-${gradientId})`}
          style={{
            transition: animated ? 'stroke-dashoffset 0.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
          }}
        />

        {/* End cap glow */}
        {percentage > 0 && (
          <circle
            cx={svgSize / 2 + radius * Math.cos((Math.PI * 2 * (percentage / 100)) - Math.PI / 2)}
            cy={svgSize / 2 + radius * Math.sin((Math.PI * 2 * (percentage / 100)) - Math.PI / 2)}
            r={actualStrokeWidth / 2}
            fill={color}
            filter={`url(#glow-${gradientId})`}
          />
        )}
      </svg>

      <div className="nx-progress-arc__content">
        {children || (
          <>
            {showValue && (
              <span
                className="nx-progress-arc__value"
                style={{ fontSize: config.fontSize }}
              >
                {Math.round(percentage)}%
              </span>
            )}
            {showLabel && label && (
              <span className="nx-progress-arc__label">{label}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProgressArc;
